/*
  The messaging screens' view shapes, and the translation from
  `{sendgrid,twilio}/messaging/*` into them.

  Two route families, one shape: `sendgrid/messaging/*` reports email and
  `twilio/messaging/*` reports SMS. Both screens render from this module — the
  SendGrid screen for mail, the Twilio screen for SMS — so nothing here is
  written for one vendor: a "recipient" is an address or a phone number
  depending on the payload, and the reason codes are whichever vendor's came
  back.

  Labels come from each payload's `source` and `message_type`, never from the
  route we called or a hardcoded vendor name. That is the rule that survives the
  backend moving a channel: the screen reports what the data says it is.
*/

import { naNumber, percent } from './deep-dive-adapters'
import type {
  MessagingAlerts,
  MessagingChannel,
  MessagingHourBucket,
  MessagingMetric,
  MessagingOverview,
  MessagingSource,
  RejectionOutcome,
  RejectionRow,
  RejectionsPage,
} from './api/deep-dive.types'

/* -------------------------------------------------------------- stat tiles */

export interface DeliveryStat {
  id: string
  label: string
  /** Already formatted — "41", "100%", or "n/a" when the provider can't report it. */
  value: string
  tone: 'healthy' | 'degraded' | 'critical' | 'info' | 'neutral'
  /** Movement against the previous window. Absent when `delta_pct` was null. */
  change?: { value: string; direction: 'up' | 'down' }
  /**
   * Used in place of `change` when the tile has something to say rather than a
   * number to move — here, why a figure is unavailable.
   */
  caption?: { icon: 'warning' | 'clock'; text: string }
  /** Outline the tile — reserved for the one figure that is a problem now. */
  highlight?: boolean
  /**
   * True when a rise is bad news (rejections, spam reports). Drives the arrow's
   * color: the same "↑ 5%" is green on Sent and red on Spam Reports.
   */
  inverted?: boolean
  /** Hover text — the payload's own caveats about how this figure was counted. */
  hint?: string
}

/**
 * A delivery block's heading, built from the payload:
 * "SendGrid · sendgrid, email · battery_calc_confirmation".
 *
 * Every part is read off `source` and `message_type` rather than from the family
 * we called. The payload is the only one of the two that knows which provider
 * actually sent the traffic and what it counted — and that is the half a reader
 * needs, since SendGrid and Twilio fail in completely different ways.
 */
export function sourceHeading(
  source: MessagingSource | undefined,
  messageType: string | undefined,
  /** Used only until the first payload lands. */
  fallback: { provider: string; channel: MessagingChannel },
): string {
  const provider = source?.provider ?? fallback.provider
  const channel = source?.channel ?? fallback.channel
  const name = source?.display_name ?? titleCase(provider)
  return [name, `${provider}, ${channel}`, messageType].filter(Boolean).join(' · ')
}

/** "sendgrid" → "SendGrid", "twilio" → "Twilio". */
function titleCase(value: string): string {
  const known: Record<string, string> = { sendgrid: 'SendGrid', twilio: 'Twilio' }
  return known[value.toLowerCase()] ?? value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * A metric's movement, or nothing.
 *
 * `delta_pct: null` means the comparison could not be made — usually because the
 * previous window predates sync coverage — so the arrow is dropped rather than
 * drawn at 0%, which would claim the figure held steady.
 */
function toChange(metric: MessagingMetric | undefined): DeliveryStat['change'] {
  const pct = metric?.delta_pct
  if (typeof pct !== 'number' || pct === 0) return undefined
  return { value: percent(Math.abs(pct)), direction: pct > 0 ? 'up' : 'down' }
}

/** Shown in place of the arrow when the provider reported nothing at all. */
const NO_DATA_CAPTION = { icon: 'clock', text: 'Not reported for this window' } as const

/**
 * Rejection rate above which the sending reputation is at stake.
 *
 * 5% is SendGrid's own guidance for bounces: past it, receiving domains start
 * treating the sender as suspect, so the tile stops being a statistic and
 * becomes a job. It is what turns the tile red and gives it the outline.
 */
const HIGH_REJECTED_PCT = 5

/**
 * The four counters for one delivery block.
 *
 * Both families answer with the same eight metrics, so this is one function for
 * both screens — the channel only decides the wording and the fourth tile:
 *
 * · **Email** (SendGrid): Sent · Bounce Backs · Deliverability · Spam Reports.
 * · **SMS** (Twilio): Sent · Failed · Deliverability · Delivered.
 *
 * The rejection tile is named for the mechanism that actually exists on the
 * channel — mail bounces, SMS simply fails; there is no bounce on a phone
 * network — and it shows the backend's own `rejected_pct` rather than a ratio
 * derived here, with the underlying count on the sub-line. Deriving it locally
 * is how this tile and the alert banner end up disagreeing by a rounding step.
 *
 * The fourth tile differs because `spam_reports` is *always* null on SMS: Twilio
 * publishes no spam-report feed, and carrier filtering (error 30007) is counted
 * under rejected rather than dressed up as spam. A tile that can only ever read
 * "n/a" is a quarter of the row spent on nothing, so SMS shows `delivered`
 * instead — a real figure — and Twilio's own note explaining the absence stays
 * in the block's info tooltip.
 *
 * Avg delivery time and open rate are not here: no field carries either and
 * neither is derivable. Beside live figures a plausible-looking constant is
 * indistinguishable from a measurement, so they are not rendered at all.
 */
export function toDeliveryStats(
  overview: MessagingOverview | undefined,
  /** Used only until the first payload lands; `source.channel` wins after that. */
  fallbackChannel: MessagingChannel,
): DeliveryStat[] {
  const window = overview?.window ?? '24h'
  const isSms = (overview?.source.channel ?? fallbackChannel) === 'sms'
  // The notes explain how each figure was counted ("sent: exact — SendGrid Email
  // Activity messages with subject…"), which is exactly the sort of caveat that
  // belongs on hover rather than in the layout.
  const hint = overview?.notes?.join('\n')

  const tile = (
    id: string,
    label: string,
    metric: MessagingMetric | undefined,
    rest: Partial<DeliveryStat>,
  ): DeliveryStat => ({
    id,
    label,
    value: naNumber(metric?.value),
    tone: 'neutral',
    change: toChange(metric),
    caption: metric && metric.value === null ? NO_DATA_CAPTION : undefined,
    hint,
    ...rest,
  })

  const rejectedCount = overview?.rejected.value
  const rejectedPct = overview?.rejected_pct.value
  const spam = overview?.spam_reports.value
  const deliverability = overview?.deliverability_pct.value
  const high = typeof rejectedPct === 'number' && rejectedPct > HIGH_REJECTED_PCT

  return [
    tile('sent', `Sent (${window})`, overview?.sent, { tone: 'healthy' }),
    tile('rejected-pct', isSms ? `Failed (${window})` : 'Bounce Backs', overview?.rejected_pct, {
      value: typeof rejectedPct === 'number' ? percent(rejectedPct) : 'n/a',
      tone: high ? 'critical' : rejectedPct ? 'degraded' : 'healthy',
      inverted: true,
      // The rate is the figure that matters; the count is what someone chases.
      caption:
        typeof rejectedCount === 'number'
          ? {
              icon: high ? 'warning' : 'clock',
              text: high
                ? `${rejectedCount.toLocaleString()} rejected — over the ${HIGH_REJECTED_PCT}% threshold`
                : `${rejectedCount.toLocaleString()} rejected`,
            }
          : undefined,
      highlight: high,
    }),
    tile('deliverability', 'Deliverability', overview?.deliverability_pct, {
      // A ratio, not a health verdict — blue keeps it from being read as "good"
      // or "bad" at a glance the way the others are.
      tone: 'info',
      value: typeof deliverability === 'number' ? percent(deliverability) : 'n/a',
    }),
    isSms
      ? tile('delivered', `Delivered (${window})`, overview?.delivered, { tone: 'healthy' })
      : tile('spam-reports', 'Spam Reports', overview?.spam_reports, {
          tone: spam ? 'critical' : 'healthy',
          inverted: true,
          // The one figure that is a problem the moment it is non-zero.
          highlight: Boolean(spam),
        }),
  ]
}

/* ------------------------------------------------------- sent vs rejected */

export interface TrafficBucket {
  /** Stable key: the ISO hour, or the ISO date when rolled up to a day. */
  key: string
  /** Axis label — "07:00" for an hour, "Sun 7" for a day. */
  label: string
  /** The full description, for the bar's tooltip. */
  caption: string
  sent: number
  rejected: number
}

export interface TrafficSeries {
  granularity: 'hour' | 'day'
  buckets: TrafficBucket[]
}

/*
  Everything below is formatted in UTC rather than local time: these bars get
  read next to the incident log and the vendor's own console, both of which are
  UTC, and an axis that silently shifted by the reader's offset would make the
  three disagree.
*/
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Above this many hourly buckets the chart switches to a bar per day.
 *
 * 48 keeps the 24h and 48h windows hourly — where every bar is legible and the
 * hour is the thing being asked about — and rolls up the 7d window, which the
 * API answers with 168 hourly buckets. Drawn as they arrive, those are 168
 * slivers under an axis that reads "00:00" seven times over.
 */
const DAILY_ABOVE = 48

/**
 * Hourly buckets → the series the stacked chart draws, at whatever granularity
 * the window deserves.
 *
 * Days are grouped by UTC calendar date, not by 24-hour blocks counted back
 * from now. That means the first and last day of a 7d window are partial — the
 * window runs 07:00 to 06:00 — but it also means a bar labelled "Sun" contains
 * Sunday. Blocks would have given exactly seven bars with weekday names that
 * were quietly wrong, which is the worse trade on a chart people read for "what
 * happened at the weekend".
 */
export function toTrafficSeries(buckets: MessagingHourBucket[] | undefined): TrafficSeries {
  const raw = buckets ?? []
  if (raw.length <= DAILY_ABOVE) {
    return {
      granularity: 'hour',
      buckets: raw.map((bucket) => ({
        key: bucket.hour,
        label: `${bucket.hour.slice(11, 13)}:00`,
        caption: `${describeDay(bucket.hour)} ${bucket.hour.slice(11, 13)}:00 UTC`,
        sent: bucket.sent,
        rejected: bucket.rejected,
      })),
    }
  }

  const byDate = new Map<string, TrafficBucket>()
  for (const bucket of raw) {
    const date = bucket.hour.slice(0, 10)
    const day = byDate.get(date)
    if (day) {
      day.sent += bucket.sent
      day.rejected += bucket.rejected
    } else {
      const at = new Date(bucket.hour)
      byDate.set(date, {
        key: date,
        label: `${WEEKDAYS[at.getUTCDay()]} ${at.getUTCDate()}`,
        caption: `${describeDay(bucket.hour)} UTC`,
        sent: bucket.sent,
        rejected: bucket.rejected,
      })
    }
  }
  return { granularity: 'day', buckets: [...byDate.values()] }
}

/** "Sun 7 Sep" — the date part shared by both captions. */
function describeDay(iso: string): string {
  const at = new Date(iso)
  return `${WEEKDAYS[at.getUTCDay()]} ${at.getUTCDate()} ${MONTHS[at.getUTCMonth()]}`
}

/* -------------------------------------------------------------- rejections */

export interface Rejection {
  id: string
  timestamp: string
  /** Masked upstream: "p***@domain.com" for email, "***-***-2882" for SMS. */
  recipient: string
  /** The vendor's own code, raw so it matches their console: "30006", "Bounced Address". */
  reasonCode: string
  description: string
  /** Drives the badge — see `outcomeClass` in `RejectionsTable`. */
  outcome: RejectionOutcome
  /** Hover text explaining what the outcome means on this channel. */
  outcomeHint: string
}

export function toRejections(page: RejectionsPage | undefined): Rejection[] {
  return (page?.rejections ?? []).map(toRejection)
}

/** What each outcome means, in the words of the channel it came from. */
const OUTCOME_HINT: Record<RejectionOutcome, string> = {
  blocked: 'The far end refused us',
  carrier_filtered: 'The carrier filtered the message (Twilio 30007)',
  bounced: 'The address rejected the mail',
  dropped: 'The provider declined to send — usually a suppression list',
  unknown: 'No outcome reported; on SMS this includes STOP replies (21610)',
}

function toRejection(row: RejectionRow, index: number): Rejection {
  return {
    // The rows carry no id of their own, and two can share a timestamp — the
    // outcome and index disambiguate without inventing a stable-looking key.
    id: `${row.timestamp}-${row.outcome}-${index}`,
    timestamp: row.timestamp.slice(0, 19).replace('T', ' '),
    recipient: row.recipient_masked,
    reasonCode: row.reason_code ?? '—',
    description: row.reason ?? 'No reason reported',
    outcome: row.outcome,
    outcomeHint: OUTCOME_HINT[row.outcome] ?? OUTCOME_HINT.unknown,
  }
}

/* ------------------------------------------------------------------ alerts */

export interface MessagingAlert {
  title: string
  actionLabel: string
  /** The vendor's own console — this is not something we can fix from here. */
  actionHref: string
}

/**
 * The blockage banner, or nothing.
 *
 * Returns null unless `blockage_suspected` is true, and the wording says
 * "suspected" because that is what the payload is: an inference drawn from
 * rejection rate, reputation and suppression counts, not a verdict the vendor
 * handed us. Claiming certainty here would send someone to a console to fix a
 * problem that may not exist.
 */
export function toMessagingAlert(alerts: MessagingAlerts | undefined): MessagingAlert | null {
  if (!alerts?.blockage_suspected) return null

  // Vendor and console both come off the payload. The link differs per channel —
  // SendGrid's statistics page versus Twilio's SMS logs — and sending someone to
  // the wrong vendor's console mid-incident is worse than not linking at all.
  const vendor = titleCase(alerts.source?.provider ?? 'the provider')
  const rate = alerts.rejection_rate_pct
  const detail =
    typeof rate === 'number' ? ` — ${percent(rate)} rejected over ${alerts.window}` : ''
  const signals = alerts.evidence?.signals?.length ? ` (${alerts.evidence.signals.join(', ')})` : ''

  return {
    title: `Account blockage suspected on ${vendor}${detail}${signals}`,
    actionLabel: `Resolve on ${vendor}`,
    actionHref: alerts.console_url,
  }
}
